/**
 * Convert one budget into a quote (spec § Quote handoff): quote draft → POST /v4/quotes → link.
 * A quote that was created but not linked is remembered in memory and localStorage (shared by the browser's tabs),
 * so a retry — even after a reload or in another tab — only links it: this flow never creates a second quote for
 * the same estimate. A quote that no longer exists (404) is forgotten so the user can start over.
 */
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import * as estimatesApi from '../api';
import type { CreatedQuote, Estimate, EstimateDetail } from '../types';
import { estimateKeys } from './useEstimates';

export type ConvertStep = 'choose' | 'working' | 'link_failed' | 'done';
interface PendingLink { quote: CreatedQuote; budgetKey: string }

const storageKey = (estimateId: string) => `estimate-convert:${estimateId}`;

function readPending(estimateId: string): PendingLink | null {
  try {
    const raw = localStorage.getItem(storageKey(estimateId));
    return raw ? (JSON.parse(raw) as PendingLink) : null;
  } catch {
    return null;
  }
}

function writePending(estimateId: string, pending: PendingLink | null) {
  try {
    if (pending) localStorage.setItem(storageKey(estimateId), JSON.stringify(pending));
    else localStorage.removeItem(storageKey(estimateId));
  } catch {
    // Storage unavailable: the in-memory state still prevents a second quote while the page is open.
  }
}

export function useConvertToQuote(estimateId: string) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingLink | null>(() => readPending(estimateId));
  const [step, setStep] = useState<ConvertStep>(() => (readPending(estimateId) ? 'link_failed' : 'choose'));
  const [error, setError] = useState<unknown>(null);
  const [estimate, setEstimate] = useState<Estimate | null>(null);

  const run = useCallback(async ({ budgetKey, accountId }: { budgetKey: string; accountId?: string | null }) => {
    setStep('working');
    setError(null);
    let link = pending;
    if (!link) {
      try {
        const payload = await estimatesApi.getQuoteDraft(estimateId, budgetKey, accountId ?? undefined);
        const quote = await estimatesApi.createQuote(payload);
        link = { quote, budgetKey };
        setPending(link);
        writePending(estimateId, link);
      } catch (e) {
        setError(e);
        setStep('choose');
        return null;
      }
    }
    try {
      const linked = await estimatesApi.linkQuote(estimateId, link.quote.id, link.budgetKey);
      writePending(estimateId, null);
      setPending(null);
      setEstimate(linked);
      setStep('done');
      queryClient.setQueryData<EstimateDetail>(estimateKeys.detail(estimateId), (old) => (old ? { ...old, estimate: linked } : old));
      void queryClient.invalidateQueries({ queryKey: estimateKeys.lists() });
      return { estimate: linked, quote: link.quote };
    } catch (e) {
      setError(e);
      if (e instanceof ApiError && e.status === 404) {
        // The created quote is gone (or the estimate is): forget it so the user can start over.
        writePending(estimateId, null);
        setPending(null);
        setStep('choose');
      } else {
        setStep('link_failed');
      }
      return null;
    }
  }, [estimateId, pending, queryClient]);

  return { step, error, pendingQuote: pending?.quote ?? null, pendingBudgetKey: pending?.budgetKey ?? null, estimate, run };
}
